<template>
  <div class="card">
    <header class="bandeau-modal card-header"></header>
    <div class="card-content">
      <error-msg-component
        :messages="messagesError"
        :error="isError"
        :messagesChampsVide="messagesChampsVide"
        :champsVide="champsVide"
        :withEmplyFields="true"
        title="general.problemOccurred"
        @close="isError = $event"/>
      <header class="has-text-centered">
        <p class="is-size-5 has-text-weight-bold">
          {{ $t('detailCommande.indexation.popUp.title') }}
        </p>
      </header>
      <section class="mgt-10px">
        <resume-indexation :item-indexe="commande" :libelle="libelleIndiceCommande" />
        <div class="tab mgt-20px">
          <ul>
            <li :class="{'is-active': activeTab === 'indexation'}"
                @click="activeTab = 'indexation'">
              {{ $t('detailCommande.indexation.popUp.tabIndexation') }}
            </li>
            <li :class="{'is-active': activeTab === 'echeancier'}"
                @click="activeTab = 'echeancier'">
              {{ $t('detailCommande.indexation.popUp.tabEcheancier',
                { nb: echeanciers.length }) }}
            </li>
          </ul>
        </div>
        <div v-if="mountedLoadingEnd">
          <form-indexation
            v-if="activeTab === 'indexation'"
            :commande="commande"
            :payload="payload"
            :indiceEtValeurs="indiceEtValeurs"
          />
          <echeancier-indexe
            v-if="activeTab === 'echeancier'"
            :commande="commande"
            :payload="payload"
            :echeanciers="echeanciers"
          />
        </div>
      </section>
      <footer class="mgt-10px">
        <div class="buttons">
          <button
            class="button has-text-centered"
            @click="$emit('close')">
            {{ $t('general.button.cancel') }}
          </button>
          <b-tooltip :active="canNotIndexer"
            :label="$t('detailCommande.indexation.errorIndexationMsg')">
            <button
              :disabled="canNotIndexer"
              class="button is-primary has-text-centered"
              @click="indexeCommande">
              {{ $t('general.button.valider') }}
            </button>
          </b-tooltip>
        </div>
      </footer>
    </div>
  </div>
</template>
<script>
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import { createNamespacedHelpers } from 'vuex';
import { getAll } from '@/api/indiceEtValeur/';
import { indexation } from '@/api/commande/index';
import translate from '@/services/devisCommandes/validation/indexationCommandeTranslator';
import validation from '@/services/devisCommandes/validation/validationIndexationCommande';
import messageErreur from '@/services/general/messageErreur';
import messageSucces from '@/services/general/messageSucces';
import formatDateIso from '@/services/general/formatIsoDate';
import errorMsgComponent from '@/components/general/errorMsgComponent';
import FormIndexation from '@/components/bodac/dcf/indexation/popUp/indexationCommandeForm';
import ResumeIndexation from '@/components/bodac/dcf/indexation/popUp/indexationCommandeResume';
import EcheancierIndexe from '@/components/bodac/dcf/indexation/popUp/indexationCommandeEcheance';
import validationGeneral from '@/services/validation/validation';
import { NO_PAGINATION } from '@/configuration/clientTableConf';
import { CATEGORIE_TLPE, CATEGORIE_HONORAIRE, CATEGORIE_DIVERS } from '@/configuration/catalogue/produitConf';
import formatDate from '@/services/general/formatDate';

const Societe = createNamespacedHelpers('societe');

export default {
  props: {
    commande: {
      type: Object,
      required: true,
    },
  },
  components: {
    errorMsgComponent,
    FormIndexation,
    ResumeIndexation,
    EcheancierIndexe,
  },
  data() {
    return {
      activeTab: 'indexation',
      indiceEtValeurs: [],
      messagesChampsVide: [],
      champsVide: [],
      isError: false,
      mountedLoadingEnd: false,
      messagesError: [],
      payload: {
        typesProduits: [],
        pourcentageReel: null,
        dateIndexation: new Date(),
        valeurIndexationActuel: null,
        dateIndexationActuel: null,
      },
    };
  },
  computed: {
    ...Societe.mapGetters({
      societeActive: 'societeActive',
    }),
    libelleIndiceCommande() {
      return this.indiceEtValeurs.find(indice => indice.id === this.commande.indiceReferenceId)?.libelle || '';
    },
    echeanciers() {
      const lignes = [
        ...this.commande.lignesCommande,
        ...this.commande.lignesCommande.flatMap(l => l.lignesCommande),
      ];
      return this.commande.echeanciers.flatMap(el => {
        const ligne = lignes.find(l => l.referenceEcheancier === el.referenceEcheancier);
        return el.echeancierLignes.map(ech => ({
          produit: ligne.produit?.libelle,
          dateDebut: ech.dateDebut,
          dateFin: ech.dateFin,
          montant: ech.montant,
          referenceEcheancier: el.referenceEcheancier,
          payeur: this.getPayeur(ech.tiersId),
          typesProduit: this.getCategoriesProduit(ligne.produit),
          facturationFaite: ech.facturationFaite,
        }))
          .filter(l => moment(l.dateDebut).isAfter(this.payload.dateIndexation))
          .filter(l => l.typesProduit.some(t => this.payload.typesProduits.includes(t)));
      });
    },
    canNotIndexer() {
      const valeurIndexationReference = this.commande.valeurIndexationActuel
        ?? this.commande.valeurIndexationReference;
      const dateIndexationReference = this.commande.dateIndexationActuel
        ?? this.commande.dateIndexationReference;
      return (this.echeanciers.filter(ech => !ech.facturationFaite).length === 0)
        || (valeurIndexationReference === this.payload.valeurIndexationActuel
        && formatDate(dateIndexationReference) === formatDate(this.payload.dateIndexationActuel));
    },
  },
  methods: {
    async indexeCommande() {
      this.isError = false;
      this.messagesChampsVide = [];
      this.champsVide = [];
      this.messagesError = [];
      const loadingComponent = this.$buefy.loading.open();
      const payload = cloneDeep(this.payload);
      payload.dateIndexation = formatDateIso(payload.dateIndexation);
      payload.dateIndexationActuel = formatDateIso(payload.dateIndexationActuel);
      try {
        await indexation({
          commandeIds: [this.commande.id],
          indexationInput: payload,
        });
        messageSucces(this.$t('detailCommande.indexation.successIndexationMsg'));
        this.$emit('reload');
        this.$parent.close();
      } catch (e) {
        this.isError = true;
        if (e.response && e.response.data && e.response.data.error) {
          validation(e.response, this.messagesError);
          validationGeneral(this.$i18n, e.response, this.champsVide,
            this.messagesError, this.messagesChampsVide, translate);
          this.affichageMessagesErreur = true;
          throw e;
        }
        messageErreur(this.$i18n);
        throw e;
      } finally {
        loadingComponent.close();
      }
    },
    async getIndiceEtValeur() {
      const loadingComponent = this.$buefy.loading.open();
      try {
        const { items } = (await getAll({
          limit: NO_PAGINATION,
        })).data.result;
        this.indiceEtValeurs = items;
      } catch (e) {
        messageErreur(this.$i18n);
        throw e;
      } finally {
        loadingComponent.close();
      }
    },
    initPayload() {
      this.payload.typesProduits = this.societeActive.produitsIndexable;
      const findIndice = this.indiceEtValeurs
        .find(el => el.id === this.commande.indiceReferenceId);
      if (findIndice) {
        const last = findIndice.indiceValeurs
          .sort((a, b) => new Date(b.dateReference) - new Date(a.dateReference))[0];
        this.payload.dateIndexationActuel = last ? new Date(last.dateReference) : null;
        this.payload.valeurIndexationActuel = last?.valeurIndice || null;
        this.payload.dateIndexation = last ? new Date(last.dateReference) : new Date();
      }
    },
    getPayeur(tiersId) {
      return this.commande.intervenants
        .find(l => l.tiersId === tiersId && l.payeur)
        ?.nomCommercial || '';
    },
    getCategoriesProduit(produit) {
      let categorie = produit?.supportCommercial.categorieVente;
      if (categorie !== CATEGORIE_DIVERS) return [categorie];
      categorie = [];
      if (produit?.produitDivers.honoraire) categorie.push(CATEGORIE_HONORAIRE);
      if (produit?.produitDivers.tlpe) categorie.push(CATEGORIE_TLPE);
      if (categorie.length === 0) categorie.push(CATEGORIE_DIVERS);
      return categorie;
    },
  },
  async mounted() {
    await this.getIndiceEtValeur();
    this.initPayload();
    this.mountedLoadingEnd = true;
  },
};
</script>
